import { useEffect, useRef, useState } from "react";
import { PageHeader } from "../components/ui/PageHeader";
import { Toast } from "../components/ui/Toast";
import { useNative } from "../../shared/phone/media";
import { useCan } from "../context/AuthContext";
import { ConnectionsNative } from "./connections/ConnectionsNative";
import { ProductConnections } from "./connections/ProductConnections";
import { copyText, EXAMPLE_TABS, exampleFor, KEY_PLACEHOLDER, useSalesTracking, type ExampleTab } from "./connections/useSalesTracking";
import "../styles/connections.css";

/** Connections: each product's accounts, and confirming sales for partners. A phone and an iPad draw it natively. */
export function Connections() {
  return useNative() ? <ConnectionsNative /> : <ConnectionsWide />;
}

function ConnectionsWide() {
  const [toast, setToast] = useState({ msg: "", show: false, done: true });
  const toastTimer = useRef<number | undefined>(undefined);
  const blockCopyTimer = useRef<number | undefined>(undefined);

  function showToast(msg: string, done = true) {
    setToast({ msg, show: true, done });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast((t) => ({ ...t, show: false })), 2900);
  }

  const sales = useSalesTracking(showToast);
  const { loaded, err, keySet, freshKey, keyRevealed, setKeyRevealed, keyBusy, maskedKey, stripeConnected, stripeKeyHint, stripeKey, setStripeKey, stripeBusy, stripeErr } = sales;
  const may = useCan()("admin");
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [tab, setTab] = useState<ExampleTab>("curl");
  const [videoOpen, setVideoOpen] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  // The API (any-checkout) path is revealed on demand; opened automatically when a
  // key already exists so it stays visible.
  const [apiOpen, setApiOpen] = useState(false);
  const keyWasSet = useRef(keySet);
  keyWasSet.current = keySet;
  useEffect(() => {
    if (loaded) setApiOpen(keyWasSet.current);
  }, [loaded]);

  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
      if (blockCopyTimer.current) clearTimeout(blockCopyTimer.current);
    },
    [],
  );

  const connectStripe = () => void sales.connectStripe();
  const disconnectStripe = () => void sales.disconnectStripe();
  function generateKey(rotate: boolean) {
    setConfirmRotate(false);
    void sales.generateKey(rotate);
  }

  async function copyBlock(id: string, text: string) {
    try {
      await copyText(text);
      setCopiedId(id);
      showToast("Copied to clipboard");
      if (blockCopyTimer.current) clearTimeout(blockCopyTimer.current);
      blockCopyTimer.current = window.setTimeout(() => setCopiedId(null), 1600);
    } catch {
      showToast("Copy failed. Select and copy manually", false);
    }
  }

  const exampleKey = freshKey ?? KEY_PLACEHOLDER;
  const exampleCode = exampleFor(tab, exampleKey);

  return (
    <>
      <section className="wrap connect">
        <PageHeader
          title="Connections"
          back={{ to: "/command", label: "Command Center" }}
          sub="The accounts each product's goals use. Flashadz checks each one itself; nothing is ticked by hand."
        />

        <ProductConnections />

        {err && <div className="load-note">Couldn’t load connections: {err}</div>}
        {!loaded && !err && <div className="load-note">Loading…</div>}

        {loaded && !err && (
          <>
            {/* PARTNERS ZONE (violet): credit partners for the sales they drive */}
            <div className="cn-zone cn-zone--partners">
              <div className="cn-zone-head">
                <span className="cn-zone-dot" aria-hidden="true" />
                <span className="cn-zone-t">Partners</span>
                <span className="cn-zone-why">Credit partners for the sales they bring.</span>
              </div>
              <div className="cn-zone-body">
                <div className="cn-track cn-report">
                  <div className="cn-track-head">
                    <div className="cn-track-ico" aria-hidden="true">
                      <svg viewBox="0 0 24 24">
                        <rect x="3" y="6" width="18" height="12" rx="2.4" />
                        <path d="M3 10h18" />
                      </svg>
                    </div>
                    <div className="cn-track-copy">
                      <b>Confirm sales</b>
                      <p>So the partner who drove a sale gets their reward. Read only, no code.</p>
                    </div>
                    <span className={`cn-key-pill${stripeConnected ? " on" : ""}`}>
                      <i className={stripeConnected ? "cn-livedot" : "cn-key-dot"} />
                      {stripeConnected ? "Connected" : "Not connected"}
                    </span>
                  </div>

                  {/* Why: setting up how sales are confirmed is an Admin's; anyone else sees the state above. */}
                  {may && (
                  <>
                  {stripeConnected ? (
                    <div className="cn-key">
                      <div className="cn-key-row">
                        <code className="cn-key-val">{stripeKeyHint}</code>
                        <span className="cn-key-note">Read-only Stripe key</span>
                      </div>
                      <div className="cn-stripe-foot">
                        <span className="cn-stripe-listen">
                          <i className="cn-livedot" /> Listening for sales.
                        </span>
                        <div className="cn-stripe-btns">
                          <button
                            className="cn-watch"
                            type="button"
                            onClick={() => setVideoOpen(true)}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                              <circle cx="12" cy="12" r="9" />
                              <path d="M10.5 9.2l4.5 2.8-4.5 2.8z" fill="currentColor" stroke="none" />
                            </svg>
                            Watch setup
                          </button>
                          <button
                            className="cn-btn-ghost"
                            type="button"
                            onClick={disconnectStripe}
                            disabled={stripeBusy}
                          >
                            {stripeBusy ? "Working…" : "Disconnect"}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <button type="button" className="cn-video" onClick={() => setVideoOpen(true)}>
                        <span className="cn-vtag">Video placeholder</span>
                        <span className="cn-play" aria-hidden="true">
                          <svg viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </span>
                        <span className="cn-vlabel">Watch: 60-second setup</span>
                      </button>

                      <div className="cn-steps">
                        <div className="cn-step">
                          <span className="cn-num">1</span>
                          <div className="cn-sbody">
                            <h4>Make a read-only key</h4>
                            <div className="cn-perm">
                              <span>Checkout Sessions</span>
                              <span>Charges</span>
                              <span>Promotion codes</span>
                            </div>
                            <a
                              className="cn-verify cn-step-btn"
                              href="https://dashboard.stripe.com/apikeys/create"
                              target="_blank"
                              rel="noopener"
                            >
                              Open Stripe
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M7 17L17 7M17 7H8M17 7v9" />
                              </svg>
                            </a>
                          </div>
                        </div>
                        <div className="cn-step">
                          <span className="cn-num">2</span>
                          <div className="cn-sbody">
                            <h4>Paste it here</h4>
                            <p>We check it and start confirming sales.</p>
                            <div className="cn-keyinput">
                              <input
                                type="text"
                                placeholder="rk_live_..."
                                value={stripeKey}
                                onChange={(e) => setStripeKey(e.target.value)}
                                aria-label="Stripe read-only key"
                              />
                              <button
                                className="cn-verify"
                                type="button"
                                onClick={connectStripe}
                                disabled={stripeBusy || !stripeKey.trim()}
                              >
                                {stripeBusy ? "Checking…" : "Connect"}
                              </button>
                            </div>
                            {stripeErr && <p className="cn-stripe-err">{stripeErr}</p>}
                          </div>
                        </div>
                      </div>

                      <div className="cn-stripe-assure">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
                          <path d="M9 12l2 2 4-4" />
                        </svg>
                        Read only. It can never move money or change your account.
                      </div>
                    </>
                  )}

                  {/* Not on Stripe? Confirm sales from any checkout with a signed key */}
                  <button
                    className={`cn-reveal${apiOpen ? " open" : ""}`}
                    type="button"
                    aria-expanded={apiOpen}
                    onClick={() => setApiOpen((v) => !v)}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                    Not on Stripe? Confirm sales from any checkout
                  </button>

                  {apiOpen && (
                    <div className="cn-apiblock">
                      <p className="cn-api-lead">
                        Post each sale to Flashadz with a secret key. The order id keeps repeats from
                        counting twice, so partner rewards stay safe from fakes.
                      </p>

              <div className="cn-key">
                {freshKey ? (
                  <>
                    <div className="cn-key-row">
                      <code className="cn-key-val fs-norecord">{keyRevealed ? freshKey : maskedKey}</code>
                      <button
                        className="cn-btn-ghost"
                        type="button"
                        onClick={() => setKeyRevealed((v) => !v)}
                      >
                        {keyRevealed ? "Hide" : "Reveal"}
                      </button>
                      <button
                        className={`cn-btn-ghost${copiedId === "key" ? " ok" : ""}`}
                        type="button"
                        onClick={() => copyBlock("key", freshKey)}
                      >
                        {copiedId === "key" ? "Copied" : "Copy"}
                      </button>
                    </div>
                    <p className="cn-key-warn">
                      Copy this now. For your security, you will not be able to see it again.
                    </p>
                  </>
                ) : keySet ? (
                  <div className="cn-key-row">
                    <code className="cn-key-val">{maskedKey}</code>
                    <span className="cn-key-note">Saved. Generate a new key to replace it.</span>
                  </div>
                ) : (
                  <p className="cn-key-note">No key yet. Generate one to start confirming sales.</p>
                )}

                <div className="cn-key-actions">
                  {!keySet ? (
                    <button
                      className="cn-verify"
                      type="button"
                      onClick={() => generateKey(false)}
                      disabled={keyBusy}
                    >
                      Generate key
                    </button>
                  ) : confirmRotate ? (
                    <>
                      <button
                        className="cn-verify danger"
                        type="button"
                        onClick={() => generateKey(true)}
                        disabled={keyBusy}
                      >
                        Confirm new key
                      </button>
                      <button
                        className="cn-btn-ghost"
                        type="button"
                        onClick={() => setConfirmRotate(false)}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      className="cn-btn-ghost"
                      type="button"
                      onClick={() => setConfirmRotate(true)}
                      disabled={keyBusy}
                    >
                      Rotate key
                    </button>
                  )}
                </div>
                {confirmRotate && (
                  <p className="cn-key-warn">
                    Rotating makes a new key and stops the old one working right away. Update your
                    backend after.
                  </p>
                )}
              </div>

              <div className="cn-tabs" role="tablist" aria-label="Ways to report a sale">
                {EXAMPLE_TABS.map((t) => (
                  <button
                    key={t.id}
                    className={`cn-tab${tab === t.id ? " on" : ""}`}
                    type="button"
                    role="tab"
                    aria-selected={tab === t.id}
                    onClick={() => setTab(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="cn-snippet">
                <pre className="cn-code fs-norecord">{exampleCode}</pre>
                <button
                  className={`cn-copy${copiedId === "example" ? " ok" : ""}`}
                  type="button"
                  onClick={() => copyBlock("example", exampleCode)}
                  aria-label="Copy example"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="9" y="9" width="11" height="11" rx="2" />
                    <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                  </svg>
                  <span className="cn-copy-label">{copiedId === "example" ? "Copied" : "Copy"}</span>
                </button>
              </div>

              <span className="cn-track-hint">
                Send the order id, amount, and coupon. The order id keeps repeats from counting
                twice.
              </span>
                    </div>
                  )}
                  </>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {videoOpen && (
          <div className="cn-vmodal" onClick={(e) => { if (e.target === e.currentTarget) setVideoOpen(false); }}>
            <div className="cn-vbox">
              <button className="cn-vclose" type="button" onClick={() => setVideoOpen(false)} aria-label="Close">&#10005;</button>
              <div className="cn-video cn-video-static">
                <span className="cn-vtag">Video placeholder</span>
                <span className="cn-play" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg></span>
                <span className="cn-vlabel">Connect Stripe in 60 seconds</span>
              </div>
            </div>
          </div>
        )}
      </section>
      <Toast message={toast.msg} on={toast.show} done={toast.done} />
    </>
  );
}
